import { useMemo } from 'react';
import { generateHTML } from '@tiptap/core';

import {
  getExtensions,
  isRichtextEmpty,
} from 'approot/shared/richtext/richtext.utils';
import {
  SUB_ACTIVITY_INSTRUCTION_SETTINGS,
  ViewProps,
} from './sub-activity.constants';
import * as Styled from './sub-activity-view.style';
import { formatSubActivityLabel } from './sub-activity.utils';
import SettingsPresentComponentMenu from 'approot/activity-builder/settings/present/settings-present-component-menu';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useTTSContext } from 'approot/activity-builder/tts/tts.context';
import { ttsGetTextBlockSelector } from 'approot/activity-builder/tts/logic/tts-get-text-block-selector';
import TTSMenuItem from 'approot/activity-builder/tts/tts-menu-item';
import TTSButton from 'approot/activity-builder/tts/tts-button';

const SubActivityView = ({
  children,
  subActivityDetails,
  ariaLabel,
  componentId,
  componentType,
  withTTSButton,
  onClick,
}: ViewProps) => {
  const { activeComponent } = useActivityBuilderPresentContext();
  const { parsedText } = useTTSContext({ id: componentId });

  const extensions = useMemo(
    () => getExtensions(SUB_ACTIVITY_INSTRUCTION_SETTINGS),
    []
  );

  const richtext = useMemo(() => {
    return parsedText
      ? parsedText
      : isRichtextEmpty(subActivityDetails?.instructions)
      ? ''
      : generateHTML(subActivityDetails?.instructions, extensions);
  }, [parsedText, extensions, subActivityDetails]);

  if (!subActivityDetails) return null;

  const content = (
    <Styled.SubActivityLabelWrapper data-selector="component-question">
      {subActivityDetails.subActivityLabel && (
        <Styled.SubActivityLabel id={`subactivity-${componentId}-label`}>
          {formatSubActivityLabel(subActivityDetails.subActivityLabel)}
        </Styled.SubActivityLabel>
      )}
      {subActivityDetails.areInstructionsVisible && (
        <Styled.SubActivityInstruction
          id={componentId ? ttsGetTextBlockSelector(componentId) : undefined}
          dangerouslySetInnerHTML={{
            __html: richtext,
          }}
        />
      )}
    </Styled.SubActivityLabelWrapper>
  );

  return (
    <SettingsPresentComponentMenu
      isActive={!!(activeComponent && activeComponent.id === componentId)}
      menuItems={
        !withTTSButton && componentId && componentType ? (
          <TTSMenuItem
            componentId={componentId}
            componentType={componentType}
          />
        ) : null
      }
    >
      {withTTSButton && componentId && componentType && (
        <TTSButton componentId={componentId} componentType={componentType} />
      )}
      <Styled.SubActivity id={`${componentId}-subactivity`}>
        {onClick ? (
          <Styled.SubActivityButton
            type="button"
            aria-label={ariaLabel}
            aria-haspopup
            onClick={onClick}
          >
            {content}
          </Styled.SubActivityButton>
        ) : (
          content
        )}

        {children}
      </Styled.SubActivity>
    </SettingsPresentComponentMenu>
  );
};

export default SubActivityView;
